import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { schools as schoolsApi, coAdmins as coAdminsApi } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  ArrowLeft, Loader2, Shield, Users, UserCog, Send, Mail, Clock, RefreshCw, XCircle, Trash2, AlertTriangle
} from 'lucide-react';
import { format } from 'date-fns';
import BrandLogo from '@/components/BrandLogo';
import AppFooter from '@/components/AppFooter';

const fmtDate = (iso) => {
  if (!iso) return '\u2014';
  try { return format(new Date(iso), 'MMM d, yyyy'); } catch { return '\u2014'; }
};

export default function CoAdminManagement() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [school, setSchool] = useState(null);
  const [summary, setSummary] = useState(null);
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showInviteConfirm, setShowInviteConfirm] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // {user_id, name, email}
  const [isRemoving, setIsRemoving] = useState(false);

  const loadSummary = useCallback(async () => {
    const res = await coAdminsApi.list(schoolId);
    setSummary(res.data);
  }, [schoolId]);

  useEffect(() => {
    const init = async () => {
      try {
        const [schoolRes] = await Promise.all([schoolsApi.get(schoolId)]);
        setSchool(schoolRes.data);
        await loadSummary();
      } catch (error) {
        const status = error?.response?.status;
        if (status === 403) {
          toast.error('Only the school owner can manage co-admins.');
        } else {
          toast.error('Could not load co-admins.');
        }
        navigate('/employer');
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [schoolId, navigate, loadSummary]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const limitReached = summary?.limit_reached;
  const inviteDisabled = !emailValid || limitReached || isSending;

  const openInviteConfirm = (e) => {
    e.preventDefault();
    if (!emailValid) { toast.error('Enter a valid email address.'); return; }
    if (limitReached) return;
    setShowInviteConfirm(true);
  };

  const handleSendInvite = async () => {
    setIsSending(true);
    try {
      await coAdminsApi.invite(schoolId, email.trim().toLowerCase());
      toast.success(`Co-admin invite sent to ${email.trim().toLowerCase()}`);
      setEmail('');
      setShowInviteConfirm(false);
      await loadSummary();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || 'Could not send invite.') : (detail || 'Could not send invite.');
      toast.error(msg);
      setShowInviteConfirm(false);
    } finally {
      setIsSending(false);
    }
  };

  const handleResend = async (inviteId) => {
    try {
      await coAdminsApi.resendInvite(schoolId, inviteId);
      toast.success('Co-admin invite resent.');
      await loadSummary();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not resend co-admin invite.');
    }
  };

  const handleCancel = async (inviteId) => {
    try {
      await coAdminsApi.cancelInvite(schoolId, inviteId);
      toast.success('Co-admin invite cancelled.');
      await loadSummary();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not cancel co-admin invite.');
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    setIsRemoving(true);
    try {
      await coAdminsApi.remove(schoolId, removeTarget.user_id);
      toast.success(`${removeTarget.name || removeTarget.email} removed as a co-admin.`);
      setRemoveTarget(null);
      await loadSummary();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not remove co-admin.');
    } finally {
      setIsRemoving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  const capLabel = summary?.unlimited ? 'unlimited' : summary?.max_co_admins;
  const activeCount = summary?.active_count ?? 0;
  const pendingCount = summary?.pending_count ?? 0;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to={`/employer/school/${schoolId}`} className="text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <BrandLogo variant="on-white" className="h-7" />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8 space-y-6">
        {/* Title + explanation */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Shield className="h-6 w-6 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Manage Co-Admins</h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Co-admins can help manage this school &mdash; post positions, manage subs, and view analytics.
              They cannot change the subscription plan, manage billing, or invite/remove other co-admins.
            </p>
            {school?.name && (
              <p className="text-xs text-slate-500 mt-1">School: <span className="font-medium text-slate-700">{school.name}</span></p>
            )}
          </div>
        </div>

        {/* Invite form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCog className="h-5 w-5 text-emerald-700" /> Invite a co-admin
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Plan cap banner */}
            <div className={`rounded-lg border p-3 text-sm ${limitReached ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`} data-testid="co-admin-cap-banner">
              {limitReached ? (
                <span>You&apos;ve reached the co-admin limit for your plan. Upgrade to invite more co-admins.</span>
              ) : (
                <span>
                  Your <strong>{summary?.plan_name}</strong> plan allows <strong>{String(capLabel)}</strong> co-admin(s).
                  You currently have <strong>{activeCount}</strong> active + <strong>{pendingCount}</strong> pending.
                </span>
              )}
            </div>

            <form onSubmit={openInviteConfirm} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="co-admin-email">Invite a co-admin by email</Label>
                <Input
                  id="co-admin-email"
                  type="email"
                  placeholder="colleague@school.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={limitReached}
                  data-testid="co-admin-email-input"
                />
              </div>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={inviteDisabled} data-testid="send-co-admin-invite-btn">
                <Send className="h-4 w-4 mr-2" /> Send co-admin invite
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Pending co-admin invites */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-slate-500" /> Pending co-admin invites
            </CardTitle>
            <CardDescription>Co-admin invitations that haven&apos;t been accepted yet.</CardDescription>
          </CardHeader>
          <CardContent>
            {pendingCount === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center" data-testid="pending-co-admins-empty">
                No pending co-admin invites.
              </p>
            ) : (
              <div className="divide-y divide-slate-100">
                {summary.pending.map((p) => (
                  <div key={p.invite_id} className="flex flex-col sm:flex-row sm:items-center gap-3 py-3" data-testid="pending-co-admin-row">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{p.email}</p>
                        <p className="text-xs text-slate-500">Sent {fmtDate(p.created_at)} &middot; Expires {fmtDate(p.expires_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={p.status === 'expired' ? 'text-amber-700 border-amber-300' : 'text-slate-600'}>
                        {p.status === 'expired' ? 'Expired' : 'Pending'}
                      </Badge>
                      <Button variant="outline" size="sm" onClick={() => handleResend(p.invite_id)} data-testid="resend-co-admin-invite-btn">
                        <RefreshCw className="h-3.5 w-3.5 mr-1" /> Resend invite
                      </Button>
                      <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={() => handleCancel(p.invite_id)} data-testid="cancel-co-admin-invite-btn">
                        <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel invite
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active co-admins */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-700" /> Active co-admins
            </CardTitle>
            <CardDescription>People who currently co-manage this school.</CardDescription>
          </CardHeader>
          <CardContent>
            {activeCount === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center" data-testid="active-co-admins-empty">
                No active co-admins yet. Invite a co-admin above to help manage this school.
              </p>
            ) : (
              <div className="divide-y divide-slate-100">
                {summary.active.map((a) => (
                  <div key={a.user_id} className="flex items-center gap-3 py-3" data-testid="active-co-admin-row">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Shield className="h-4 w-4 text-emerald-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{a.name || a.email}</p>
                      <p className="text-xs text-slate-500 truncate">{a.email} &middot; Accepted {fmtDate(a.accepted_at)}</p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Co-Admin</Badge>
                    <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={() => setRemoveTarget(a)} data-testid="remove-co-admin-btn">
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove co-admin
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Invite confirmation dialog */}
      <Dialog open={showInviteConfirm} onOpenChange={setShowInviteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-emerald-700" /> Send co-admin invite?
            </DialogTitle>
            <DialogDescription>
              You are inviting <strong>{email.trim().toLowerCase()}</strong> to be a <strong>co-admin</strong> of
              <strong> {school?.name}</strong>. They&apos;ll be able to help manage this school but cannot change billing or manage other co-admins.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowInviteConfirm(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSendInvite} disabled={isSending} data-testid="confirm-send-co-admin-invite-btn">
              {isSending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Send co-admin invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove co-admin confirmation dialog */}
      <Dialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-600" /> Remove {removeTarget?.name || removeTarget?.email} as a co-admin
            </DialogTitle>
            <DialogDescription>
              This will remove <strong>{removeTarget?.name || removeTarget?.email}</strong> as a co-admin of
              <strong> {school?.name}</strong>. They will lose access to manage this school. You can invite them again later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setRemoveTarget(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={handleRemove} disabled={isRemoving} data-testid="confirm-remove-co-admin-btn">
              {isRemoving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Remove co-admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AppFooter role="employer" />
    </div>
  );
}
